import { useTranslations } from "next-intl";
import * as React from "react";

import {
  Button,
  ModalDialogBody,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogRoot,
  Text,
} from "@zapier/design-system";

export interface ConfirmModalDisplayProps {
  cancelLabel?: string;
  confirmLabel?: string;
  description?: string;
  title: string;
}

export interface ConfirmModalProps extends ConfirmModalDisplayProps {
  onCancel: () => void;
  onConfirm: () => void;
}

interface ConfirmModalProviderProps {
  children: React.ReactNode;
}

type ConfirmFunction = (props: ConfirmModalDisplayProps) => Promise<boolean>;

export function ConfirmModal(props: ConfirmModalProps) {
  const t = useTranslations("common");

  return (
    <ModalDialogRoot
      aria-label={props.title}
      isOpen={true}
      onOpenChange={(open) => {
        if (!open) props.onCancel();
      }}
      width="600px"
    >
      <ModalDialogHeader>{props.title}</ModalDialogHeader>
      <ModalDialogBody>
        <Text>{props.description}</Text>
      </ModalDialogBody>
      <ModalDialogFooter>
        <Button variant="secondary" onPress={props.onCancel}>
          {props.cancelLabel || t("cancel")}
        </Button>
        <Button variant="primary" onPress={props.onConfirm}>
          {props.confirmLabel || t("confirm")}
        </Button>
      </ModalDialogFooter>
    </ModalDialogRoot>
  );
}

const Context = React.createContext<ConfirmFunction | undefined>(undefined);

export function ConfirmModalProvider({ children }: ConfirmModalProviderProps) {
  const [isOpen, setIsOpen] = React.useState(false);

  /** Called when a user confirms, cancels, or closes the modal */
  const actionCallback = React.useRef<
    ((modalResult: boolean) => void) | undefined
  >(undefined);

  /** The modal props passed to `confirm()` */
  const modalProps = React.useRef<ConfirmModalDisplayProps | undefined>(
    undefined
  );

  /** Show the modal and await a response */
  const confirm = React.useCallback((props: ConfirmModalDisplayProps) => {
    modalProps.current = props;
    setIsOpen(true);

    // Wait on the user to confirm or cancel
    return new Promise<boolean>((resolve) => {
      // When the user responds, close the modal and resolve the promise
      actionCallback.current = (modalResult) => {
        setIsOpen(false);
        resolve(modalResult);
      };
    });
  }, []);

  return (
    <Context.Provider value={confirm}>
      {children}

      {modalProps.current && isOpen && (
        <ConfirmModal
          {...modalProps.current}
          onCancel={() => actionCallback.current?.(false)}
          onConfirm={() => actionCallback.current?.(true)}
        />
      )}
    </Context.Provider>
  );
}

/**
 * A hook to show a confirm modal.
 *
 * Returns a function to show a modal and returns a promise that resolves to a boolean.
 * `true` means the user confirmed, `false` means they canceled or closed the modal.
 */
export function useConfirm() {
  const confirm = React.useContext(Context);

  if (!confirm) {
    throw new Error(
      "`useConfirm` must be used within a `<ConfirmModalProvider>`."
    );
  }

  return confirm;
}
